import React, { useMemo, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";
import SectionHead from "../ui/SectionHead";
import { projects } from "../../data/projects";
import { ease, trackSpotlight } from "../../utils/motionPresets";
import "./projects.css";

const categories = ["All", ...new Set(projects.map((project) => project.category))];

const hostname = (url) => new URL(url).hostname.replace(/^www\./, "");

const ProjectCard = ({ project, index, featured }) => {
  const [hovered, setHovered] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const springY = useSpring(y, { stiffness: 300, damping: 30 });

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    x.set(event.clientX - rect.left);
    y.set(event.clientY - rect.top);
  };

  return (
    <motion.article
      layout
      className={`card project ${featured ? "project--featured" : ""}`}
      onPointerMove={trackSpotlight}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.6, ease }}
    >
      <Link
        to={`/work/${project.id}`}
        className="project__media"
        aria-label={`${project.title} case study`}
        onPointerMove={handleMove}
        onPointerEnter={(event) => event.pointerType === "mouse" && setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        <div className="project__browser">
          <div className="project__browser-bar" aria-hidden="true">
            <span className="project__browser-dots">
              <i />
              <i />
              <i />
            </span>
            <span className="project__browser-url mono">{hostname(project.link)}</span>
          </div>
          <div className="project__shot">
            <img src={project.gallery.desktop[0]} alt="" loading="lazy" />
          </div>
        </div>

        <AnimatePresence>
          {hovered && (
            <motion.span
              className="project__cursor"
              style={{ x: springX, y: springY }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease }}
              aria-hidden="true"
            >
              View <FiArrowRight />
            </motion.span>
          )}
        </AnimatePresence>
      </Link>

      <div className="project__info">
        <div className="project__meta mono">
          <span>{String(index + 1).padStart(2, "0")}</span>
          <span className="project__category">
            {project.category} · {project.context}
          </span>
        </div>
        <h3 className="project__title">
          <Link to={`/work/${project.id}`}>
            {project.title}
            <FiArrowRight className="project__title-icon" aria-hidden="true" />
          </Link>
        </h3>
        <p className="project__desc">{project.summary}</p>
        <ul className="project__tags" aria-label="Technologies">
          {project.stack.slice(0, 4).map((tech) => (
            <li key={tech.name} className="chip">
              {tech.name}
            </li>
          ))}
        </ul>
        <div className="project__actions">
          <Link to={`/work/${project.id}`} className="project__action project__action--primary">
            Case study <FiArrowRight />
          </Link>
          <a
            href={project.link}
            target="_blank"
            rel="noreferrer"
            className="project__action"
            aria-label={`${project.title} live site (opens in a new tab)`}
          >
            Live site <FiArrowUpRight />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

const Projects = () => {
  const [filter, setFilter] = useState("All");

  const visible = useMemo(
    () => (filter === "All" ? projects : projects.filter((p) => p.category === filter)),
    [filter]
  );

  const countFor = (category) =>
    category === "All" ? projects.length : projects.filter((p) => p.category === category).length;

  return (
    <section className="section" id="work">
      <div className="container">
        <SectionHead
          index="03"
          eyebrow="Selected work"
          title={
            <>
              Products live
              <br />
              in <span className="serif">production.</span>
            </>
          }
          lede="Platforms, e-commerce and corporate sites I've engineered and shipped — open any project for the full case study, on desktop and phone."
        />

        <div className="filters" role="group" aria-label="Filter projects by category">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={`filter ${filter === category ? "is-active" : ""}`}
              aria-pressed={filter === category}
              onClick={() => setFilter(category)}
            >
              {filter === category && (
                <motion.span
                  layoutId="filter-pill"
                  className="filter__pill"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <span className="filter__label">{category}</span>
              <span className="filter__count mono">{countFor(category)}</span>
            </button>
          ))}
        </div>

        <motion.div layout className="projects">
          <AnimatePresence mode="popLayout">
            {visible.map((project, i) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={projects.indexOf(project)}
                featured={filter === "All" && i === 0}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
